import React, { useMemo } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { PayItem, PayTag } from '../../utils/feePaySelection';

interface InvoicePickerProps {
  items: PayItem[];
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onSelectAll: (all: boolean) => void;
  format: (amount: number) => string;
}

const TAG_COLORS: Record<PayTag, { bg: string; text: string }> = {
  Overdue: { bg: '#FEE2E2', text: '#B91C1C' },
  'Due soon': { bg: '#FEF3C7', text: '#B45309' },
  Upcoming: { bg: '#E0F2FE', text: '#0369A1' },
};

/**
 * The pending invoices on the "Pay fees" sheet (app and web): name, due date, amount and
 * status for each, ticked to include it in the amount above (utils/feePaySelection).
 */
export function InvoicePicker({ items, selected, onToggle, onSelectAll, format }: InvoicePickerProps) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const allSelected = items.length > 0 && items.every(i => selected.has(i.id));

  return (
    <View testID="fee-invoice-picker">
      <View style={styles.headRow}>
        <Text style={styles.headText}>Invoices to pay</Text>
        {items.length > 1 && (
          <TouchableOpacity onPress={() => onSelectAll(!allSelected)} accessibilityRole="button">
            <Text style={styles.link}>{allSelected ? 'Clear all' : 'Select all'}</Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.list}>
        {items.map((item, index) => {
          const on = selected.has(item.id);
          const tag = TAG_COLORS[item.tag];
          return (
            <TouchableOpacity
              key={item.id}
              style={[styles.row, index > 0 && styles.rowDivider, on && styles.rowOn]}
              onPress={() => onToggle(item.id)}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: on }}
              accessibilityLabel={`${item.label}, ${item.dueText}, ${format(item.amount)}`}
              testID={`fee-pick-${item.id}`}
            >
              <FeatherIcon
                name={on ? 'check-square' : 'square'}
                size={20}
                color={on ? colors.primary : colors.textMuted}
              />
              <View style={styles.info}>
                <Text style={styles.label} numberOfLines={2}>{item.label}</Text>
                <View style={styles.metaRow}>
                  <Text style={styles.due}>{item.dueText}</Text>
                  <View style={[styles.tag, { backgroundColor: tag.bg }]}>
                    <Text style={[styles.tagText, { color: tag.text }]}>{item.tag}</Text>
                  </View>
                </View>
              </View>
              <Text style={styles.amount}>{format(item.amount)}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  headRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  headText: { fontSize: 13, fontWeight: '700', color: colors.textSecondary },
  link: { fontSize: 13, fontWeight: '700', color: colors.primary },
  list: { borderWidth: 1, borderColor: colors.border, borderRadius: 14, overflow: 'hidden', backgroundColor: colors.surface },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 12, paddingVertical: 12 },
  rowDivider: { borderTopWidth: 1, borderTopColor: colors.border },
  rowOn: { backgroundColor: colors.background },
  info: { flex: 1, minWidth: 0 },
  label: { fontSize: 14, fontWeight: '700', color: colors.textPrimary },
  metaRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 4 },
  due: { fontSize: 12, color: colors.textSecondary },
  tag: { paddingHorizontal: 6, paddingVertical: 1, borderRadius: 6 },
  tagText: { fontSize: 10.5, fontWeight: '800' },
  amount: { fontSize: 14, fontWeight: '800', color: colors.textPrimary },
});
